import type { TFunction } from "i18next";
import type { DateTime } from "luxon";
import type { CallOffShiftParams } from "../../../hooks/useCallOffShift";
import type { RemovalDetails, RemoveNursesEntry } from "../../../hooks/useHandleNurseChange";
import { ChangesSource, type WeekData } from "../../../utils/models";
import { formatShiftDate, formatShiftHours } from "../../AddNurse/addStaffUtils";
import { findShiftOnDay, type RemoveStaffRow, type RemoveStaffShiftRef } from "./removeStaffRows";
import { joinNames, pickLabel, SINGLE_REMOVE_COUNT } from "./removeStaffUtils";

/** One picked shift, resolved against the loaded schedule: what the publish and the local copy act on. */
export type RemovalEntry = { row: RemoveStaffRow; ref: RemoveStaffShiftRef; name: string };

/**
 * The picks as loaded shifts. A pick the loaded week no longer holds (someone else removed it, a refresh
 * raced the panel) is reported as missing rather than skipped, so the removal never silently does less.
 */
export const resolveRemovalEntries = (
	rows: readonly RemoveStaffRow[],
	week: WeekData,
	dayNo: number,
): { entries: RemovalEntry[]; missing: RemoveStaffRow[] } => {
	const entries: RemovalEntry[] = [];
	const missing: RemoveStaffRow[] = [];
	for (const row of rows) {
		const ref = findShiftOnDay(week, dayNo, row.shiftId);
		if (ref) entries.push({ row, ref, name: row.name });
		else missing.push(row);
	}
	return { entries, missing };
};

/** What one removal does with each pick: published as a removal, or called off (with a replacement search). */
export type RemovalPlan = { publish: RemovalEntry[]; callOff: RemovalEntry[] };

/**
 * "Search for replacements" sends the in-window picks through the call-off route; "just remove" — and every
 * pick outside the window — goes into the one publish.
 */
export const planRemoval = (entries: readonly RemovalEntry[], inWindow: readonly RemovalEntry[], searchForReplacement: boolean): RemovalPlan => {
	const callOff = searchForReplacement ? [...inWindow] : [];
	const callOffIds = new Set(callOff.map((entry) => entry.row.shiftId));
	return { publish: entries.filter((entry) => !callOffIds.has(entry.row.shiftId)), callOff };
};

/** A publish entry, addressed by the shift's own bucket. */
export const toRemoveNursesEntry = ({ ref }: RemovalEntry, weekIndex: number, dayNo: number): RemoveNursesEntry => ({
	nurse: ref.shift,
	shiftIndex: ref.shiftIndex,
	dayNo,
	weekIndex,
});

/**
 * A call-off by a manager from the staffing panel. The source matters: without it the route records a nurse
 * calling herself off, and she is never told.
 */
export const toCallOffParams = ({ ref }: RemovalEntry, details: RemovalDetails): CallOffShiftParams => {
	if (!details.message) throw new Error("A call-off needs the removal reason, which the footer requires before this point");
	return {
		departmentId: ref.shift.departmentId,
		shiftId: ref.shift.id,
		nurseId: ref.shift.nurseId,
		reason: details.message,
		changeSource: ChangesSource.Staffing,
		removalMessage: details.removalMessage,
	};
};

type OutcomeArgs = {
	removed: readonly RemovalEntry[];
	failed: readonly RemovalEntry[];
	/** Of the removed, how many were called off with a replacement search. */
	searchedCount: number;
	staffingDate: DateTime;
	t: TFunction;
};

export type RemovalOutcome = { severity: "success" | "error"; message: string };

const successMessage = ({ removed, searchedCount, staffingDate, t }: Omit<OutcomeArgs, "failed">): string => {
	const date = formatShiftDate(staffingDate);
	const [only] = removed;
	const removedText =
		removed.length === SINGLE_REMOVE_COUNT
			? t("remove.staff.success.one", { name: only.name, date, hours: formatShiftHours({ start: only.ref.shift.startTime, end: only.ref.shift.endTime }) })
			: t("remove.staff.success.many", { count: removed.length, date });
	return searchedCount > 0 ? `${removedText} ${t("remove.staff.success.searching", { count: searchedCount })}` : removedText;
};

/**
 * The ONE message a removal ends with: who came off (and whether cover is being searched for), or — when any
 * shift failed — who is still on it, which stays picked for another try. Each failed shift is named with its
 * hours, so a nurse with two shifts on the date reads as two. On a partial failure each sentence agrees with its
 * own count: "2 staff were removed." over the removed, "Couldn't remove Cy … — they're still selected." over the failed.
 */
export const describeRemovalOutcome = (args: OutcomeArgs): RemovalOutcome => {
	const { removed, failed, t } = args;
	if (failed.length === 0) return { severity: "success", message: successMessage(args) };
	const names = joinNames(
		failed.map((entry) => pickLabel(entry.name, { start: entry.ref.shift.startTime, end: entry.ref.shift.endTime }, t)),
		t,
	);
	if (removed.length === 0) return { severity: "error", message: t("remove.staff.error.none", { count: failed.length, names }) };
	const removedText = t("remove.staff.error.some.removed", { count: removed.length });
	const failedText = t("remove.staff.error.some.failed", { count: failed.length, names });
	return { severity: "error", message: `${removedText} ${failedText}` };
};
